import {
  Box,
  Flex,
  Progress,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text
} from '@chakra-ui/react'

export interface IFormStep {
  key: string
  title: string
}

export interface IFormStepperProps {
  steps: IFormStep[]
  /** Index of the current step in `steps`. */
  activeIndex: number
  /**
   * Going back by clicking a step already done. Only the earlier steps are clickable: moving
   * forward is the form's "Avançar", which saves the step first.
   */
  onStepClick?: (index: number) => void
}

/**
 * The steps of a multi-step form, at the top of it: all of them, the current one and the ones
 * already done. On a phone the row does not fit, so it becomes "Etapa X de Y" over a bar.
 *
 * Colour comes from the theme (`Stepper` is magenta there), like the other toggles of the panel.
 */
export default function FormStepper({ steps, activeIndex, onStepClick }: IFormStepperProps) {
  const active = steps[activeIndex]

  return (
    <Box mb={6}>
      <Stepper index={activeIndex} size="sm" gap={2} display={{ base: 'none', md: 'flex' }}>
        {steps.map((step, index) => {
          const canGoBack = Boolean(onStepClick) && index < activeIndex
          const content = (
            <>
              <StepIndicator>
                <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
              </StepIndicator>
              <StepTitle>
                <Text as="span" fontSize="xs" lineHeight="short" display="block" maxW="96px" textAlign="left">
                  {step.title}
                </Text>
              </StepTitle>
            </>
          )

          return (
            <Step key={step.key}>
              {canGoBack ? (
                <Flex
                  as="button"
                  type="button"
                  align="center"
                  gap={2}
                  onClick={() => onStepClick?.(index)}
                  aria-label={`Voltar para ${step.title}`}
                  borderRadius="md"
                  _hover={{ opacity: 0.8 }}
                >
                  {content}
                </Flex>
              ) : (
                <Flex align="center" gap={2} aria-current={index === activeIndex ? 'step' : undefined}>
                  {content}
                </Flex>
              )}
              <StepSeparator />
            </Step>
          )
        })}
      </Stepper>

      <Box display={{ base: 'block', md: 'none' }}>
        <Text variant="caption">
          Etapa {activeIndex + 1} de {steps.length}
        </Text>
        <Text fontWeight="semibold" mb={2}>
          {active?.title}
        </Text>
        <Progress
          value={((activeIndex + 1) / steps.length) * 100}
          size="xs"
          colorScheme="magenta"
          borderRadius="full"
          aria-label={`Etapa ${activeIndex + 1} de ${steps.length}`}
        />
      </Box>
    </Box>
  )
}
